export function bodyScroll(): number {
  if (typeof window.pageYOffset != "undefined") {
    //most browsers except IE before #9
    return window.pageYOffset;
  } else {
    const B = document.body; //IE 'quirks'
    let D = document.documentElement; //IE with doctype
    D = D.clientHeight ? D : B;
    return D.scrollTop;
  }
}

export function scrollToNew(y: number, scrollElement?: HTMLElement | null) {
  if (scrollElement) {
    scrollElement.scrollTo(0, y);
  } else {
    window.scrollTo(0, y);
  }
}

export function scrollToBottomNew(scrollElement?: HTMLElement | null) {
  if (scrollElement) {
    scrollElement.scrollTo(0, 999999999);
  } else {
    window.scrollTo(0, 999999999);
  }
}

// Restores whose first write hasn't happened yet. A view transition waits for
// these so its "after" snapshot shows the list at its restored position
// instead of at the top, followed by a jump.
let pendingRestores = 0;
const restoreWaiters = new Set<() => void>();

function settleRestore() {
  pendingRestores = Math.max(0, pendingRestores - 1);
  if (pendingRestores === 0) {
    restoreWaiters.forEach((resolve) => resolve());
  }
}

export function waitForScrollRestore(maxMs = 300): Promise<void> {
  if (pendingRestores === 0) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const finish = () => {
      clearTimeout(timer);
      restoreWaiters.delete(finish);
      resolve();
    };
    const timer = setTimeout(finish, maxMs);
    restoreWaiters.add(finish);
  });
}

const RESTORE_TIMEOUT_MS = 2000;
const RESTORE_TOLERANCE_PX = 1;
const USER_INPUT_EVENTS = ["wheel", "touchstart", "keydown", "mousedown"];

/**
 * Scrolls to `target` after the current task and keeps re-applying it while
 * `content` resizes, until the reader takes over (wheel, touch, key, mouse) or
 * the timeout passes. A single scroll right after mount isn't enough: a
 * progressively rendered list is clamped by however much of it exists at that
 * moment, and one that first renders taller than its settled height has the
 * browser's own scroll anchoring move the position as it shrinks. Returns a
 * cancel function.
 */
export function restoreScrollPosition(options: {
  target: number;
  read: () => number;
  write: (y: number) => void;
  content: Element | null;
}): () => void {
  const { target, read, write, content } = options;
  let observer: ResizeObserver | undefined;
  let timeout: ReturnType<typeof setTimeout> | undefined;

  pendingRestores++;
  let isFirstWriteSettled = false;
  const settleFirstWrite = () => {
    if (!isFirstWriteSettled) {
      isFirstWriteSettled = true;
      settleRestore();
    }
  };

  const cancel = () => {
    settleFirstWrite();
    clearTimeout(start);
    clearTimeout(timeout);
    observer?.disconnect();
    USER_INPUT_EVENTS.forEach((event) =>
      window.removeEventListener(event, cancel)
    );
  };

  const apply = () => {
    if (Math.abs(read() - target) > RESTORE_TOLERANCE_PX) write(target);
  };

  const start = setTimeout(() => {
    write(target);
    settleFirstWrite();
    if (content && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(apply);
      observer.observe(content);
    }
    USER_INPUT_EVENTS.forEach((event) =>
      window.addEventListener(event, cancel, { passive: true })
    );
    timeout = setTimeout(cancel, RESTORE_TIMEOUT_MS);
  });

  return cancel;
}
